import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  TextField,
  Tooltip,
  IconButton,
  Stack,
  Grid,
  Fab,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { Form, Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import arrayMutators from 'final-form-arrays';
import { useNavigate } from 'react-router-dom';

import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';
import FormSaveAndSubmit from '@components/FormSaveAndSubmit';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';
import { breadcrumbsActions } from '@store/breadcrumbs';
import { selectLoggedInUserEmail } from '@store/auth';
import { showSuccessMessage, showErrorMessage } from '@store/dashboard';
import { createLegacyMaintenanceSchedule } from '../../store/legacy-payment-options-maintenance-schedule.slice';
import {
  addMaintenanceSchedule,
  MAINTENANCE_SCHEDULE_LIST_ROUTE,
} from '../../legacy-payment-options.breadcrumbs';

import { PAGE_DETAILS } from '../../legacy-payment-options.constants';

const FORM_VALIDATORS = {
  required: (value) => (value ? undefined : 'This is a required field'),
};
const INITIAL_FORM = {
  startDateTime: dayjs(Date.now()).add(1, 'day').startOf('day'),
  endDateTime: dayjs(Date.now()).add(2, 'day').startOf('day'),
  tabCodes: [''],
  pmids: [''],
};

function FormArrayHeader({ title, tooltip, onAction }) {
  return (
    <Stack
      flexDirection="row"
      alignItems="center"
      gap={2}>
      <Tooltip
        title={tooltip}
        placement="left">
        <Fab
          color="primary"
          size="small"
          sx={{ boxShadow: 'none' }}
          onClick={onAction}>
          <AddIcon />
        </Fab>
      </Tooltip>
      <Typography variant="formHeader">{title}</Typography>
    </Stack>
  );
}

function FormArrayRowAction({ onRemove }) {
  return (
    <IconButton
      color="primary"
      onClick={onRemove}>
      <CloseIcon />
    </IconButton>
  );
}

export default function LegacyMaintenanceScheduleForm({ permissions = {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formValue] = useState(INITIAL_FORM);

  const userEmail = useSelector(selectLoggedInUserEmail);
  const [openDialog, setOpenDialog] = useState({ show: false });

  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(addMaintenanceSchedule));
  }, [dispatch]);

  const onStartTimeChangeHandler = (newValue, values, onChange, setValue) => {
    onChange(newValue);

    if (dayjs(values.endTime).isBefore(newValue)) {
      setValue('endDateTime', dayjs(newValue).add(1, 'day').startOf('day'));
    }
  };

  const onSubmit = ({ tabCodes, pmids, startDateTime, endDateTime }) => {
    const payload = {
      tabCodes,
      pmids,
      startDateTime,
      endDateTime,
      user: userEmail,
    };

    dispatch(createLegacyMaintenanceSchedule(payload)).then((data) => {
      if (data.payload && data.payload.status === 'SUCCESS') {
        dispatch(
          showSuccessMessage(PAGE_DETAILS.MaintenanceSchedule.CreateSuccess),
        );
        navigate(MAINTENANCE_SCHEDULE_LIST_ROUTE);
      } else {
        dispatch(
          showErrorMessage({
            message: PAGE_DETAILS.MaintenanceSchedule.CreateFailure,
            duration: 5000,
          }),
        );
      }
    });
  };

  const renderFormActions = ({
    pristine,
    submitting,
    invalid,
    form,
    permissions: userPermissions,
  }) => (
    <Grid
      item
      xs={12}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 3,
      }}>
      <FormSaveAndSubmit
        resetDisabled={pristine}
        submitDisabled={
          !userPermissions.canCreateLegacyMaintenanceSchedule ||
          submitting ||
          pristine ||
          invalid
        }
        handleReset={() => {
          form.reset();
        }}
      />
    </Grid>
  );

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <ApplicationPageTemplate pageReady>
      <Form
        onSubmit={onSubmit}
        initialValues={{ ...formValue }}
        mutators={{
          ...arrayMutators,
          setValue: ([field, value], state, { changeValue }) => {
            changeValue(state, field, () => value);
          },
        }}
        render={({
          handleSubmit,
          values,
          pristine,
          submitting,
          invalid,
          form,
          form: {
            mutators: { push, setValue },
          },
        }) => (
          <form
            onSubmit={(e) =>
              process.env.REACT_APP_ENV !== 'production'
                ? handleSubmit(e)
                : handlePreSubmit(e)
            }
            noValidate>
            <ProdConfirmationDialog
              openDialog={openDialog}
              yesHandler={handleSubmit}
            />
            <Grid
              container
              spacing={1}
              mb={2}>
              {renderFormActions({
                form,
                submitting,
                pristine,
                invalid,
                values,
                permissions,
              })}
            </Grid>
            <Grid
              container
              spacing={4}
              mb={2}>
              <Grid
                item
                xs={6}>
                <Field
                  name="startDateTime"
                  validate={FORM_VALIDATORS.required}>
                  {({ input: { name, value, onChange }, meta: { error } }) => (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        required
                        {...{ name, value }}
                        onChange={(newValue) => {
                          if (!newValue) return;

                          onStartTimeChangeHandler(
                            newValue,
                            values,
                            onChange,
                            setValue,
                          );
                        }}
                        label={
                          PAGE_DETAILS.MaintenanceSchedule.FieldStartDateTime
                        }
                        minDate={dayjs(Date.now()).add(1, 'day')}
                        slotProps={{
                          field: { fullWidth: true },
                          textField: {
                            helperText: error,
                            error: !!error,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  )}
                </Field>
              </Grid>
              <Grid
                item
                xs={6}>
                <Field
                  name="endDateTime"
                  validate={FORM_VALIDATORS.required}>
                  {({ input: { name, value, onChange }, meta: { error } }) => (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        required
                        {...{ name, value }}
                        onChange={(newValue) => {
                          if (!newValue) return;

                          onChange(newValue);
                        }}
                        label={
                          PAGE_DETAILS.MaintenanceSchedule.FieldEndDateTime
                        }
                        minDate={dayjs(values.startDateTime)}
                        slotProps={{
                          field: { fullWidth: true },
                          textField: {
                            helperText: error,
                            error: !!error,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  )}
                </Field>
              </Grid>

              <Grid
                item
                xs={12}>
                <FormArrayHeader
                  title={PAGE_DETAILS.MaintenanceSchedule.FieldTabCodesTitle}
                  tooltip={
                    PAGE_DETAILS.MaintenanceSchedule.FieldTabCodesTooltip
                  }
                  onAction={() => {
                    push('tabCodes', '');
                  }}
                />
                <FieldArray name="tabCodes">
                  {({ fields }) =>
                    fields.map((row, index) => (
                      <Stack
                        key={row}
                        flexDirection="row"
                        mt={2}>
                        <Field
                          name={`${row}`}
                          validate={FORM_VALIDATORS.required}>
                          {({ input: { name, value, onChange } }) => (
                            <TextField
                              {...{ name, value, onChange }}
                              required
                              label={`Tab code #${index + 1}`}
                              type="text"
                              sx={{ flex: 1 }}
                            />
                          )}
                        </Field>
                        <FormArrayRowAction
                          onRemove={() => fields.remove(index)}
                        />
                      </Stack>
                    ))
                  }
                </FieldArray>
              </Grid>

              <Grid
                item
                xs={12}>
                <FormArrayHeader
                  title={PAGE_DETAILS.MaintenanceSchedule.FieldPmIdTitle}
                  tooltip={PAGE_DETAILS.MaintenanceSchedule.FieldPmIdTooltip}
                  onAction={() => {
                    push('pmids', '');
                  }}
                />
                <FieldArray name="pmids">
                  {({ fields }) =>
                    fields.map((row, index) => (
                      <Stack
                        key={row}
                        flexDirection="row"
                        mt={2}>
                        <Field
                          name={`${row}`}
                          validate={FORM_VALIDATORS.required}>
                          {({ input: { name, value, onChange } }) => (
                            <TextField
                              {...{ name, value, onChange }}
                              required
                              label={`Payment Method ID #${index + 1}`}
                              type="text"
                              sx={{ flex: 1 }}
                            />
                          )}
                        </Field>
                        <FormArrayRowAction
                          onRemove={() => fields.remove(index)}
                        />
                      </Stack>
                    ))
                  }
                </FieldArray>
              </Grid>
            </Grid>
          </form>
        )}
      />
    </ApplicationPageTemplate>
  );
}
